import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";
import { AlertTriangle } from "lucide-react";
import { Logger } from "@/lib/logger/Logger";
import { TableInvoiceRow } from "../types/invoice-table.types";
import { calculatePaymentAmountForCancellation } from "../utils/payment-calculations";
import { Checkbox } from "../../../components/ui/checkbox";
import { useRemindersStore } from "../../../store/Reminders";
import { FeeInvoiceReminder } from "../../../store/Reminders/reminders.store";
import { InvoiceType } from "../../../types/database-enums/invoices-enums";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";
import { InvoiceOpenReminderFee } from "../mahnwesen/payment-reminder.types";
import { formatCurrency } from "../../../lib/utils/utils-functions";

export interface InvoiceCancellationDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (reason: string, rebookAsAkonto: boolean, cancelReminderFees: boolean) => Promise<void>;
	invoice: TableInvoiceRow | null | undefined;
	loading?: boolean;
}

export const InvoiceCancellationDialog: React.FC<InvoiceCancellationDialogProps> = ({
	isOpen,
	onClose,
	onConfirm,
	invoice,
	loading = false,
}) => {
	const paymentAmount = calculatePaymentAmountForCancellation(invoice);
	const hasAkontoPayments = invoice?.subRows?.some(subRow => subRow.allocation_type === 2 && !subRow.allocation_canceled_at) || false;
	const invoiceNumber = invoice?.invoice_doc_number || "";
	const hasPayments = (paymentAmount ?? 0) > 0;
	const isNegativeInvoice = (invoice?.invoice_value ?? 0) < 0;
	const [reason, setReason] = useState("");
	const [paymentHandling, setPaymentHandling] = useState<"cancel" | "rebook" | "">("");
	const [validationError, setValidationError] = useState("");
	const [paymentHandlingError, setPaymentHandlingError] = useState("");
	const [reminderFees, setReminderFees] = useState<InvoiceOpenReminderFee[]>([]);
	const [cancelReminderFees, setCancelReminderFees] = useState(true);
	const [feeReminder, setFeeReminder] = useState<FeeInvoiceReminder | null>(null);
	const [feesLoading, setFeesLoading] = useState(false);
	const invoiceId = invoice?.invoice_id;
	const isFeeInvoice = invoice?.invoice_type === InvoiceType.MAHNGEBUEHR;
	const isFeeOfLowerStage = feeReminder !== null && !feeReminder.isLatest;
	const reminderFeesTotal = reminderFees.reduce((sum, fee) => sum + fee.open_amount, 0);

	useEffect(() => {
		if (!isOpen || !invoiceId) return;
		let active = true;
		setFeesLoading(true);
		const store = useRemindersStore.getState();
		if (isFeeInvoice) {
			store.fetchFeeInvoiceReminder(invoiceId).then((reminder) => {
				if (!active) return;
				setFeeReminder(reminder);
				setFeesLoading(false);
			});
		} else {
			store.fetchOpenReminderFees(invoiceId).then((fees) => {
				if (!active) return;
				setReminderFees(fees);
				setFeesLoading(false);
			});
		}
		return () => {
			active = false;
		};
	}, [isOpen, invoiceId, isFeeInvoice]);

	const validateReason = (value: string): boolean => {
		const trimmedValue = value.trim();
		if (trimmedValue.length === 0) {
			setValidationError("Grund für Stornierung ist erforderlich");
			return false;
		}
		setValidationError("");
		return true;
	};

	const handleReasonChange = (value: string) => {
		setReason(value);
		if (value.trim().length > 0) {
			validateReason(value);
		} else {
			setValidationError("");
		}
	};

	const handleConfirm = async () => {
		if (!validateReason(reason)) {
			return;
		}

		// Validate payment handling if invoice has payments (except for negative invoices which can only cancel)
		if (hasPayments && !isNegativeInvoice && !paymentHandling) {
			setPaymentHandlingError("Bitte wählen Sie eine Option für die Zahlungsbehandlung");
			return;
		}

		try {
			const rebookAsAkonto = !isNegativeInvoice && paymentHandling === "rebook";
			await onConfirm(reason.trim(), rebookAsAkonto, reminderFees.length > 0 && cancelReminderFees);
			handleClose();
		} catch (error) {
			// Error handling is done by the parent component
			Logger.error("Cancellation failed:" + error);
		}
	};

	const handleClose = () => {
		setReason("");
		setPaymentHandling("");
		setValidationError("");
		setPaymentHandlingError("");
		setReminderFees([]);
		setFeeReminder(null);
		setCancelReminderFees(true);
		setFeesLoading(false);
		onClose();
	};

	const isReasonValid = reason.trim().length > 0;
	const isPaymentHandlingValid = !hasPayments || isNegativeInvoice || paymentHandling !== "";
	const canConfirm = isReasonValid && isPaymentHandlingValid && !feesLoading && !isFeeOfLowerStage;

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-red-600">
						<AlertTriangle className="h-5 w-5" />
						Rechnung stornieren
					</DialogTitle>
					<DialogDescription>
						Sie sind dabei, die Rechnung <strong>{invoiceNumber}</strong> zu stornieren.
						Bitte geben Sie einen Grund für die Stornierung an.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<Label htmlFor="cancellation-reason" className="text-sm font-medium">
							Grund für Stornierung <span className="text-red-500">*</span>
						</Label>
						<Textarea
							id="cancellation-reason"
							placeholder="Bitte geben Sie den Grund für die Stornierung an..."
							value={reason}
							onChange={(e) => handleReasonChange(e.target.value)}
							className={`resize-none ${validationError ? 'border-red-500 focus:border-red-500' : ''}`}
							rows={4}
							disabled={loading}
							data-testid="cancellation-reason-input"
						/>
						{validationError && (
							<p className="text-sm text-red-600" role="alert">
								{validationError}
							</p>
						)}
						<p className="text-xs text-gray-500">
							Grund ist erforderlich
						</p>
					</div>

					{hasPayments && (
						<div className="space-y-3 pt-2 border-t">
							<div className="bg-amber-50 border border-amber-200 rounded-md p-3">
								<p className="text-sm font-medium text-amber-900">
									Diese Rechnung hat Zahlungen in Höhe von CHF {paymentAmount.toFixed(2)}
								</p>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									Zahlungsbehandlung <span className="text-red-500">*</span>
								</Label>
								<RadioGroup
									value={paymentHandling}
									onValueChange={(value) => {
										setPaymentHandling(value as "cancel" | "rebook");
										setPaymentHandlingError("");
									}}
									disabled={loading}
								>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="cancel" id="cancel-payments" data-testid="cancel-payments-option" />
										<Label htmlFor="cancel-payments" className="font-normal cursor-pointer">
											Zahlungen {hasAkontoPayments && "& Akontozuweisungen"} stornieren
										</Label>
									</div>
									{!isNegativeInvoice && (
										<div className="flex items-center space-x-2">
											<RadioGroupItem value="rebook" id="rebook-payments" data-testid="rebook-payments-option" />
											<Label htmlFor="rebook-payments" className="font-normal cursor-pointer">
												Zahlungen als Akonto umbuchen
											</Label>
										</div>
									)}
								</RadioGroup>
								{isNegativeInvoice && (
									<p className="text-xs text-gray-600 italic">
										Gutschrift-Zahlungen können nicht als Akonto umgebucht werden
									</p>
								)}
								{paymentHandlingError && (
									<p className="text-sm text-red-600" role="alert">
										{paymentHandlingError}
									</p>
								)}
								<p className="text-xs text-gray-500">
									Bitte wählen Sie, wie mit den vorhandenen Zahlungen verfahren werden soll
								</p>
							</div>
						</div>
					)}
				</div>

				{feeReminder && (
					<p
						className={`rounded-md border p-3 text-sm ${
							isFeeOfLowerStage
								? "border-amber-200 bg-amber-50 text-amber-900"
								: "border-blue-200 bg-blue-50 text-blue-900"
						}`}
						data-testid="cancel-fee-reminder-hint"
					>
						{isFeeOfLowerStage
							? `Diese Mahngebühr gehört zur ${getReminderStageLabel(feeReminder.stage)}. Storniert werden kann nur die Mahngebühr der höchsten aktiven Mahnstufe – nehmen Sie zuerst die höheren Stufen zurück.`
							: `Die ${getReminderStageLabel(feeReminder.stage)} wird mit der Mahngebühr zurückgenommen.`}
					</p>
				)}

				{reminderFees.length > 0 && (
					<div className="flex items-center gap-2 border-t pt-3">
						<Checkbox
							id="cancel-reminder-fees"
							checked={cancelReminderFees}
							onCheckedChange={(checked) => setCancelReminderFees(checked === true)}
							disabled={loading}
							aria-label="Mahngebühren ebenfalls stornieren"
							data-testid="cancel-reminder-fees-checkbox"
						/>
						<Label htmlFor="cancel-reminder-fees" className="text-sm">
							Mahngebühren ebenfalls stornieren ({reminderFees.length}, {formatCurrency(reminderFeesTotal, invoice?.invoice_currency)})
						</Label>
					</div>
				)}

				<DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
					<Button
						variant="outline"
						onClick={handleClose}
						disabled={loading}
						data-testid="cancel-cancellation-button"
					>
						Abbrechen
					</Button>
					<Button
						variant="destructive"
						onClick={handleConfirm}
						disabled={!canConfirm || loading}
						data-testid="confirm-cancellation-button"
					>
						{loading ? "Storniere..." : "Rechnung stornieren"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};